import {
  Activity,
  CreditCard as CreditCardIcon,
  Wallet,
} from "lucide-react"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { TabsContent } from "@/components/ui/tabs"
import {
  DashboardInfoTypeEnum,
  DashboardPaymentTypeEnum,
  DashboardSmaugTypeEnum,
} from "@/enums"
import {
  useDashboard,
  useDashboardPayment,
  useDashboardSmaug,
} from "@/hooks"

import { useSidebar } from "../ui/sidebar"
import { DashboardBrandRecurringChart } from "./charts/brand-recurring"
import { DashboardPaymentMethodsChart } from "./charts/payment-methods"
import { DashboardVolumeDayWeekRecurringChart } from "./charts/volume-day-week-recurring"
import { DateRangePicker } from "./date-range-picker"
import { FinancialHealth } from "./sections/financial-health"
import { HeroMetrics } from "./sections/hero-metrics"
import { OperationalPerformance } from "./sections/operational-performance"

type DashboardRotateTabContentProps = SearchParamsType & {
  isActive: boolean
}
export const DashboardRotateTabContent: React.FC<
  DashboardRotateTabContentProps
> = ({ searchParams, isActive }) => {
  const { t } = useTranslation()
  const { open } = useSidebar()

  const {
    data: futureReceivablesRotary,
    isFetching: isLoadingFutureReceivables,
  } = useDashboardSmaug({
    ...searchParams,
    dashboardType:
      DashboardSmaugTypeEnum.FUTURE_RELEASES_ROTARY,
    enabled: isActive,
  })
  const {
    data: totalTransactionsRotary,
    isFetching: isLoadingTotalTransactions,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.TOTAL_TRANSACTIONS_ROTARY,
    enabled: isActive,
  })
  const {
    data: transactedAmountRotary,
    isFetching: isLoadingTransactedAmount,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.TRANSACTED_AMOUNT_ROTARY,
    enabled: isActive,
  })
  const {
    data: averageTicketRotary,
    isFetching: isLoadingAverageTicket,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.AVERAGE_TICKET_ROTARY,
    enabled: isActive,
  })
  const { data: reasonsRefusalRotary } =
    useDashboardPayment({
      ...searchParams,
      dashboardType:
        DashboardPaymentTypeEnum.REASONS_REFUSAL_ROTARY,
      enabled: isActive,
    })
  const {
    data: approvalIndexGeneralRotary,
    isFetching: isLoadingApprovalIndex,
  } = useDashboardPayment({
    ...searchParams,
    dashboardType:
      DashboardPaymentTypeEnum.APPROVAL_INDEX_GENERAL_ROTARY,
    enabled: isActive,
  })
  const futureReceivablesRotaryValue =
    futureReceivablesRotary?.value
      ? futureReceivablesRotary.value / 100
      : 0
  const totalTransactionsRotaryValue =
    totalTransactionsRotary?.value
      ? totalTransactionsRotary.value
      : 0
  const transactedAmountRotaryValue =
    transactedAmountRotary?.value
      ? transactedAmountRotary.value / 100
      : 0
  const averageTicketRotaryValue =
    averageTicketRotary?.value
      ? averageTicketRotary.value / 100
      : 0
  const approvalIndexGeneralValue =
    approvalIndexGeneralRotary?.indicator
      ?.find((item) => item.key === "Aprovado")
      ?.value.replace("%", "")
      ? Number(
          approvalIndexGeneralRotary.indicator
            .find((item) => item.key === "Aprovado")
            ?.value.replace("%", "") || "0"
        )
      : 0
  return (
    <TabsContent
      value="rotate"
      className="group mt-4 space-y-8"
      data-sidebar-open={open}
    >
      <div className="flex items-center justify-end">
        <DateRangePicker searchParams={searchParams} />
      </div>

      {/* Hero Metrics Section */}
      <HeroMetrics
        revenue={{
          current: transactedAmountRotaryValue,
          isLoading: isLoadingTransactedAmount,
        }}
        volume={{
          current: totalTransactionsRotaryValue,
          isLoading: isLoadingTotalTransactions,
        }}
        approvalRate={{
          current: approvalIndexGeneralValue,
          isLoading: isLoadingApprovalIndex,
        }}
        averageTicket={{
          current: averageTicketRotaryValue,
          isLoading: isLoadingAverageTicket,
        }}
      />

      <Separator />

      {/* Financial Health Section */}
      <FinancialHealth
        futureReceivables={{
          value: futureReceivablesRotaryValue,
          isLoading: isLoadingFutureReceivables,
        }}
      />

      <Separator />

      {/* Operational Performance Section */}
      <OperationalPerformance
        approvalData={{
          general: approvalIndexGeneralValue,
          firstInstallment: 0, // Rotary não tem índice separado por parcela
          otherInstallments: 0, // Rotary não tem índice separado por parcela
        }}
        refusalReasons={
          reasonsRefusalRotary?.indicator || []
        }
        approvalIndexGeneralType={
          DashboardPaymentTypeEnum.APPROVAL_INDEX_GENERAL_ROTARY
        }
        searchParams={searchParams as SearchParamsType}
      />

      <Separator />

      {/* Detailed Analytics Section */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">
            {t(
              "pages.dashboard.sections.detailedAnalytics.title",
              "Análise Detalhada"
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              "pages.dashboard.sections.detailedAnalytics.description",
              "Gráficos e métricas complementares"
            )}
          </p>
        </div>
        <section className="grid gap-6 md:grid-cols-2">
          <Card className="border-l-4 border-l-teal-500 transition-all duration-200 hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.volumeByWeekday.title"
                )}
              </CardTitle>
              <Activity className="h-5 w-5 text-teal-500" />
            </CardHeader>
            <CardContent>
              <DashboardVolumeDayWeekRecurringChart
                dashboardType={
                  DashboardSmaugTypeEnum.VOLUME_DAY_WEEK_ROTARY
                }
                searchParams={
                  searchParams as SearchParamsType
                }
              />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500 transition-all duration-200 hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.brandDistribution.title"
                )}
              </CardTitle>
              <CreditCardIcon className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <DashboardBrandRecurringChart
                dashboardType={
                  DashboardSmaugTypeEnum.MOST_USED_BRAND_ROTARY
                }
                searchParams={
                  searchParams as SearchParamsType
                }
              />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500 transition-all duration-200 hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.paymentMethods.title"
                )}
              </CardTitle>
              <Wallet className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <DashboardPaymentMethodsChart
                dashboardType={
                  DashboardPaymentTypeEnum.PAYMENT_METHODS_ROTARY
                }
                searchParams={
                  searchParams as SearchParamsType
                }
              />
            </CardContent>
          </Card>
        </section>
      </div>
    </TabsContent>
  )
}
